import React, { useState, useEffect } from 'react';
import TourBackdrop from './TourBackdrop';
import './GuidedTour.css';

function GuidedTour({ onTourComplete, onOpenWindow, onCloseWindow, onSetFocusWindow, onStepChange, onGuideMessageChange, onCurrentStepChange, onNavigate, onSystemStateChange }) {
  const [tourActive, setTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Tour step definitions with extended timing
  const tourSteps = [
    {
      id: 1,
      window: 'about',
      title: 'Meet the Operator',
      subtitle: 'Profile & Security Focus',
      message: 'This is the operator behind the system.',
      guideMessage: 'This interface represents my identity, technical focus, and security mindset.',
      systemState: 'Profile Analysis Active',
      duration: 10000,
    },
    {
      id: 2,
      window: 'projects',
      title: 'Real Security Tools',
      subtitle: 'Open Source Security Tools',
      message: 'Real security tools, real results.',
      guideMessage: 'Functional security tools addressing real-world problems.',
      systemState: 'Project Inspection Mode',
      duration: 10000,
    },
    {
      id: 3,
      window: 'contact',
      title: 'Get In Touch',
      subtitle: 'Secure Communication Channels',
      message: 'Multiple secure ways to reach me.',
      guideMessage: 'Secure channels for professional communication.',
      systemState: 'Communication Channels Available',
      duration: 10000,
      highlightDock: false
    },
    {
      id: 4,
      window: null, // No window - clean slate
      title: 'System Handover',
      subtitle: 'Manual Control',
      message: 'System checks complete.',
      guideMessage: 'GUIDED MODE DISENGAGED — MANUAL CONTROL GRANTED',
      systemState: 'Handing Over Control',
      duration: 10000,
      highlightDock: false
    }
  ];

  // Start tour function (exposed globally)
  useEffect(() => {
    window.startGuidedTour = () => {
      setTourActive(true);
      setCurrentStep(0);
    };

    return () => {
      delete window.startGuidedTour;
    };
  }, []);

  // Keyboard controls: Esc to exit
  useEffect(() => {
    if (!tourActive) return;

    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        handleSkipTour();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourActive, currentStep]);


  // Handle tour progression
  useEffect(() => {
    if (!tourActive) return;

    const step = tourSteps[currentStep];
    if (!step) return;

    // Smooth transition with consistent timing for both directions
    const handleTransition = async () => {
      // Step 1: Close previous window (if going forward) or current window (if going back)
      if (currentStep > 0) {
        const previousStep = tourSteps[currentStep - 1];
        if (previousStep.window) {
          // Small delay before closing for smooth feel
          await new Promise(resolve => setTimeout(resolve, 100));
          onCloseWindow(previousStep.window);
        }
      }

      // Step 2: Wait for close animation
      await new Promise(resolve => setTimeout(resolve, 200));

      // Step 3: Open current step's window and set focus
      if (step.window) {
        onOpenWindow(step.window);
        // Small delay to ensure window renders before focusing
        await new Promise(resolve => setTimeout(resolve, 100));
        onSetFocusWindow(step.window);
      }

      // Step 4: Handle dock highlighting
      if (step.highlightDock) {
        const dockElement = document.querySelector('.dock');
        if (dockElement) {
          dockElement.classList.add('tour-highlight');
        }
      } else {
        const dockElement = document.querySelector('.dock');
        if (dockElement) {
          dockElement.classList.remove('tour-highlight');
        }
      }
    };

    // Execute transition
    handleTransition();

    // Notify of step change (for subtitle)
    if (onStepChange) {
      onStepChange(step.subtitle || '');
    }


    // Notify of guide message change
    if (onGuideMessageChange) {
      onGuideMessageChange(step.guideMessage || '');
    }

    // Notify of system state change
    if (onSystemStateChange) {
      onSystemStateChange(step.systemState || '');
    }

    // Notify of current step change
    if (onCurrentStepChange) {
      onCurrentStepChange(currentStep, tourSteps.length);
    }

    // Expose navigation functions
    if (onNavigate) {
      onNavigate({
        next: () => {
          if (currentStep < tourSteps.length - 1) {
            setCurrentStep(currentStep + 1);
          }
        },
        previous: () => {
          if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
          }
        },
        exit: handleSkipTour
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourActive, currentStep]);



  const handleSkipTour = () => {
    completeTour();
  };

  const completeTour = () => {
    // Start fade out immediately
    setIsFadingOut(true);
    
    // After fade animation (0.5s), actually complete the tour
    setTimeout(() => {
      setTourActive(false);
      setIsFadingOut(false);
      
      // Close all tour windows
      tourSteps.forEach(step => {
        if (step.window) {
          onCloseWindow(step.window);
        }
      });

      // Remove dock highlight
      const dockElement = document.querySelector('.dock');
      if (dockElement) {
        dockElement.classList.remove('tour-highlight');
      }

      // Clear focus
      onSetFocusWindow(null);

      // Call parent completion handler
      if (onTourComplete) {
        onTourComplete();
      }
    }, 500); // Wait for fade out animation
  };

  // Render tour UI
  if (!tourActive) return null;

  return (
    <>
      {/* Backdrop to dim non-focused windows */}
      <TourBackdrop className={isFadingOut ? 'fade-out' : ''} />
    </>
  );
}

export default GuidedTour;
